'use client';

import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import {ChevronLeft, ChevronRight} from 'lucide-react';

import {ContentBlockContainer} from '@/components/content-blocks/block-container';
import type {ExtractContentBlockType} from '@/components/content-blocks/ContentBlocks';
import {TestimonialCard} from '@/components/content-blocks/testimonial-list-selection/testimonial-card';
import {usePrevNextButtons} from '@/components/image-gallery/hooks/usePrevNextButtons';
import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';

import {testimonialListFromTestimonialListSelectionSection} from './query';

type Props = ExtractContentBlockType<'testimonialListSelection'>;

export const TestimonialListSelection = (props: Props) => {
	const {heading, description, contentBlockOptions} = props ?? {};

	const testimonials =
		testimonialListFromTestimonialListSelectionSection(props);

	const prefersReducedMotion = usePrefersReducedMotion();

	const activeCarousel = testimonials && testimonials.length > 1;

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: activeCarousel,
		duration: prefersReducedMotion ? 0 : 20,
	});

	const {
		prevBtnDisabled,
		nextBtnDisabled,
		onPrevButtonClick,
		onNextButtonClick,
	} = usePrevNextButtons(emblaApi);

	return (
		<ContentBlockContainer
			options={contentBlockOptions}
			heading={heading}
			description={description}
			centered
		>
			<div
				className={clsx(
					'flex',
					activeCarousel ? 'justify-between' : 'justify-center',
					'items-center',
					'gap-4',
				)}
			>
				{activeCarousel && (
					<button
						type="button"
						onClick={onPrevButtonClick}
						disabled={prevBtnDisabled}
						aria-label="Forrige"
						className={clsx(
							'p-1',
							'md:p-2',
							'disabled:opacity-50',
							'bg-black/70',
							'rounded-full',
							'cursor-pointer',
							'disabled:pointer-events-none',
							'text-white',
						)}
					>
						<ChevronLeft width={32} height={32} aria-hidden />
					</button>
				)}
				<div className={clsx('min-w-0')}>
					<div
						ref={emblaRef}
						className={clsx(
							'flex',
							'shrink-0',
							'grow',
							'overflow-hidden',
							'min-w-0',
						)}
					>
						<ul
							className={clsx(
								'flex',
								'grow',
								'touch-pan-y',
								'touch-pinch-zoom',
								'gap-16',
								'min-w-0',
							)}
						>
							{testimonials && (
								<>
									{testimonials.map((testimonial) => {
										return (
											<li
												key={testimonial._key}
												className="relative flex shrink-0 grow-0 basis-full justify-center"
											>
												<TestimonialCard
													name={testimonial.name}
													imageWeb={
														testimonial.imageWeb
													}
													quote={testimonial.quote}
													role={testimonial.role}
													customer={
														testimonial.customer
													}
												/>
											</li>
										);
									})}
								</>
							)}
						</ul>
					</div>
				</div>
				{activeCarousel && (
					<button
						type="button"
						onClick={onNextButtonClick}
						disabled={nextBtnDisabled}
						aria-label="Neste"
						className={clsx(
							'p-1',
							'md:p-2',
							'disabled:opacity-50',
							'bg-black/70',
							'rounded-full',
							'cursor-pointer',
							'disabled:pointer-events-none',
							'text-white',
						)}
					>
						<ChevronRight width={32} height={32} aria-hidden />
					</button>
				)}
			</div>
		</ContentBlockContainer>
	);
};
